import { flyToView } from "../lmv-compat/tools/ViewTransition";
import { DT_PRIMARY_MODEL_LOADED, DT_FACETS_LOADED } from "./DtEventTypes";
import { FacetsManager, getFiltersFromFacetsViewState, getSettingsFromFacetsViewState } from "./facets/FacetsManager";

import { av, THREE } from "@adsk/lmv";

const HalfPI = Math.PI * 0.5;

function updateFovAndIsPerspective(sceneCamera, viewCamera) {
  if (viewCamera.fov !== undefined && sceneCamera.fov !== viewCamera.fov) {
    sceneCamera.setFov(viewCamera.fov);
  }

  if (viewCamera.isPerspective && !sceneCamera.isPerspective) {
    sceneCamera.toPerspective();
  } else if (!viewCamera.isPerspective && sceneCamera.isPerspective) {
    sceneCamera.toOrthographic();
  }
}

function fuzzyEqual(a, b, eps = 1e-4) {
  return Math.abs(b - a) < eps;
}

async function _resolveElementIds(facility, e) {
  const model = facility.getModelByUrn(e.modelUrn);
  if (!model) {
    console.warn("Model referenced in view not found", e.modelUrn);
    return;
  }
  await model.waitForLoad(false, true);
  const ids = await model.getDbIdsFromElementIds(e.ids);
  return {
    model,
    ids
  };
}

// TODO: this is called fire&forget which seems sloppy and likely will fail
// if views are switched quickly
function _restoreFilterOverride(facility, view) {
  facility.facetsManager.clearFilterOverrides();
  view.hiddenElements?.forEach(async (e) => {
    let res = await _resolveElementIds(facility, e);
    if (res) {
      facility.facetsManager.setHiddenElements(res);
    }
  });
  view.isolatedElements?.forEach(async (e) => {
    let res = await _resolveElementIds(facility, e);
    if (res) {
      facility.facetsManager.setIsolatedElements(res);
    }
  });
}

function _showElementsForView(facility, view, currentView) {
  function onAllFacetsLoaded() {
    // this callback will be executed when user opens some facility and switches a view while loading isn't done yet
    // we have to wait until all models for the previous view are loaded and show all elements for them, and
    // hide all hidden elements for the current view
    if (currentView) {
      _restoreFilterOverride(facility, currentView);
    }

    if (facility.eventTarget.hasEventListener(DT_FACETS_LOADED, onAllFacetsLoaded)) {
      facility.eventTarget.removeEventListener(DT_FACETS_LOADED, onAllFacetsLoaded);
    }
  }

  const someNotLoaded = facility.
  getModels().
  map((m) => !m.isLoadDone()).
  some(Boolean);

  // view loading, this.currentView could be yet undefined
  _restoreFilterOverride(facility, view);

  if (someNotLoaded) {
    facility.eventTarget.addEventListener(DT_FACETS_LOADED, onAllFacetsLoaded);
  }
}
/**
 * @param {Map<DtModel, []Number>} model2ids
 */
function resolveIds(model2ids) {
  const promises = [];

  const getElementIdsFromDbIds = async (model, dbIds) => {
    const elementIds = await model.getElementIdsFromDbIds(dbIds);
    return {
      ids: elementIds.filter((i) => i),
      modelUrn: model.urn()
    };
  };

  for (const [model, dbIds] of model2ids.entries()) {
    promises.push(getElementIdsFromDbIds(model, dbIds));
  }

  return Promise.all(promises);
}

/**
 * Update ViewCubeUi extension's current face with current camera position
 * @param {Autodesk.Viewing.Viewer3D} viewer
 */
function updateViewCubeCurrentFace(viewer) {

  // TODO: Expose this function from LMV (this code is copy-pasted from ViewCube constructor)
  const updateViewCube = () => {
    const EPSILON = 0.00001;
    const viewCubeUi = viewer.getExtension('Autodesk.Tandem.ViewCubeUi');
    const viewCube = viewCubeUi.cube;
    if (!viewCube) {
      return;
    }

    const cam = viewer.autocam;
    const camera = cam.camera;

    const viewDir = cam.center.clone().sub(camera.position).normalize();
    const sceneRight = cam.sceneFrontDirection.clone().cross(cam.sceneUpDirection);
    const dotUp = viewDir.dot(cam.sceneUpDirection);
    const dotFront = viewDir.dot(cam.sceneFrontDirection);
    const dotRight = viewDir.dot(sceneRight);

    if (1 - Math.abs(dotUp) < EPSILON) {
      viewCube.currentFace = dotUp > 0 ? 'bottom' : 'top';
    } else if (1 - Math.abs(dotFront) < EPSILON) {
      viewCube.currentFace = dotFront > 0 ? 'front' : 'back';
    } else if (1 - Math.abs(Math.abs(dotRight)) < EPSILON) {
      viewCube.currentFace = dotRight > 0 ? 'left' : 'right';
    } else {
      viewCube.currentFace = 'front';
    }
  };

  function onExtensionLoaded(evt) {
    if (evt.extensionId === 'Autodesk.Tandem.ViewCubeUi') {
      updateViewCube();
      viewer.removeEventListener(av.EXTENSION_LOADED_EVENT, onExtensionLoaded);
    }
  }

  if (viewer.getExtension('Autodesk.Tandem.ViewCubeUi')) {
    updateViewCube();
  } else {
    viewer.addEventListener(av.EXTENSION_LOADED_EVENT, onExtensionLoaded);
  }
}

function isMissingWorldUp(worldup) {
  if (!worldup) {
    return true;
  }

  return worldup.x === 0 && worldup.y === 0 && worldup.z === 0;
}

//Restore THREE.js object prototypes in case the input is raw JSON
function restoreCameraObjects(camera) {

  if (!camera) {
    return null;
  }

  return {
    position: new THREE.Vector3().copy(camera.position),
    target: new THREE.Vector3().copy(camera.target),
    up: new THREE.Vector3().copy(camera.up),
    isPerspective: camera.isPerspective,
    fov: camera.fov,
    worldup: isMissingWorldUp(camera.worldup) ?
    new THREE.Vector3(0, 0, 1) :
    new THREE.Vector3().copy(camera.worldup)
  };
}

//Restore THREE.js object prototypes in case the input is raw JSON
function restoreCutplaneObjects(cutPlanes) {

  if (!cutPlanes) {
    return [];
  }

  return cutPlanes.map(({ x, y, z, w }) => new THREE.Vector4(x, y, z, w));
}

export class DtViewerState {

  static flyToView(facility, view, duration = 2.0, transformCamera, CB) {
    facility.viewAnimControl?.stop();

    let cc = restoreCameraObjects(view.camera);
    if (transformCamera) {
      let xform = facility.getSharedToLocalSpaceTransform(view.version < 2);
      cc.position = cc.position.clone().applyMatrix4(xform);
      cc.target = cc.target.clone().applyMatrix4(xform);
      cc.up = cc.up.clone().transformDirection(xform);
    }

    // View cube currentFace is only updated when the user clicks on a face.
    // In our case, a user might save a view of a specific face (ex "Top") and we need to update the cube state
    // after the camera transition.
    // currentFace is needed by the cube to know what is the next face to show when clicking on the directional arrows
    const onFinished = () => {
      facility.viewAnimControl = null;
      updateViewCubeCurrentFace(facility.viewer);
      if (CB) {
        CB();
      }
    };

    const angle = cc.up.angleTo(facility.viewer.impl.camera.worldup);

    const worldUpAligned = !(fuzzyEqual(angle, 0.0) || fuzzyEqual(angle, HalfPI) || fuzzyEqual(angle, Math.PI));

    facility.viewAnimControl = flyToView(facility.viewer, cc, duration, onFinished, worldUpAligned);
  }

  static async updateCutPlanes(facility, view) {
    const ext = facility.viewer.getExtension('Autodesk.Tandem.Section');

    await facility.viewer.impl.waitForLoaderIdle();

    let cutPlanes = restoreCutplaneObjects(view.cutPlanes);
    cutPlanes = cutPlanes.map((v) => {
      let pl = new THREE.Plane().setComponents(v.x, v.y, v.z, v.w);
      pl.applyMatrix4(facility.getSharedToLocalSpaceTransform(view.version < 2));
      return new THREE.Vector4(pl.normal.x, pl.normal.y, pl.normal.z, pl.constant);
    });

    // Unload any existing planes first
    ext.setSectionFromPlane(null);
    if (cutPlanes?.length === 1) {
      ext.setSectionFromPlane(cutPlanes[0]);
    } else if (cutPlanes?.length === 6) {
      ext.setBoxFromPlanes(cutPlanes);
    }
  }


  static async getCurrentView(facility) {

    const camera = facility.viewer.getCamera();

    const visibilityOverrides = facility.facetsManager.getFilterOverrides();

    const hiddenElements = await resolveIds(visibilityOverrides.hidden);
    const isolatedElements = await resolveIds(visibilityOverrides.isolated);

    let cameraForView = {
      position: camera.position.clone(),
      target: camera.target.clone(),
      up: camera.up.clone(),
      isPerspective: camera.isPerspective,
      fov: camera.fov,
      worldup: camera.worldup
    };

    let cutPlanes = facility.viewer.getExtension('Autodesk.Tandem.Section')?.getSectionPlanes();

    const dataViz = facility.viewer.dataViz;

    let heatmap;
    if (dataViz) {
      heatmap = dataViz.heatmapCfgUi?.getStateForView();
    }

    let xform = facility.getLocalToSharedSpaceTransform(false);
    cameraForView.position = cameraForView.position.clone().applyMatrix4(xform);
    cameraForView.target = cameraForView.target.clone().applyMatrix4(xform);
    cameraForView.up = cameraForView.up.clone().transformDirection(xform);

    cutPlanes = cutPlanes?.map((v) => {
      let pl = new THREE.Plane().setComponents(v.x, v.y, v.z, v.w);
      pl.applyMatrix4(xform);
      return new THREE.Vector4(pl.normal.x, pl.normal.y, pl.normal.z, pl.constant);
    });

    let hud = facility.getHUD()?.getStateForView();

    const facets = facility.facetsManager.getStateForView();

    return {
      camera: cameraForView,
      cutPlanes,
      heatmap,
      hiddenElements,
      isolatedElements,
      facets,
      hud
    };
  }

  /* Creates a base view without loading the facility first. */
  static getBaseView(facility) {
    return {
      cutPlanes: [],
      heatmap: {},
      hiddenElements: [],
      isolatedElements: [],
      facets: FacetsManager.getBaseStateForView(facility)
    };
  }

  static async setView(facility, view, currentView) {
    facility.deactivateExtensions();

    view ??= {}; // A missing view should still restore components to a default state.
    facility.facetsManager.viewRestoreInProgress = true;
    facility.facetsManager.viewId = view.id;

    // Restore facets settings before any model is loaded
    // to prevent default facets from being used during asynchronous model loading
    let facetsSettings = getSettingsFromFacetsViewState(facility, view.facets);

    await facility.facetsManager.setSettings(facetsSettings, view.facets?.hiddenCategories, view.facets?.useTopLevel);

    const viewer = facility.viewer;

    const setCurrentViewImpl = async () => {

      if (!facility.globalOffset) {
        return;
      }

      // restore camera
      if (view.camera) {
        updateFovAndIsPerspective(viewer.getCamera(), view.camera);

        DtViewerState.flyToView(facility, view, undefined, true);
      }

      // Restore cut planes
      if (viewer.getExtension('Autodesk.Tandem.Section')) {
        DtViewerState.updateCutPlanes(facility, view);
      } else {
        viewer.loadExtension('Autodesk.Tandem.Section').then((sectionExt) => {
          if (!sectionExt) {
            console.warn('unexpected section extension unavailable');
            return;
          }
          DtViewerState.updateCutPlanes(facility, view);
        });
      }

      if (viewer.dataViz) {
        // Restore heatmaps
        viewer.dataViz.heatmapCfgUi?.setStateFromView(view.heatmap);
      }

      // Restore facets filters
      let filters = getFiltersFromFacetsViewState(view.facets);
      if (filters.length) {
        facility.facetsManager.setFilterMap(filters);
      } else {
        //If there is a filter in this view, it will
        //trigger updateFacets() inside setFilterMap(),
        //but if not, we call it here.
        facility.facetsManager.updateFacets();
      }

      // Restore hiddenElements
      _showElementsForView(facility, view, currentView);

      facility.facetsManager.viewRestoreInProgress = false;
    };

    // here we wait until at least the primary model's root is loaded
    // cause it will trigger the internal camera setup ("Viewer3D#setViewFromCamera()")
    // which we want to override with the view's camera position etc.
    if (!facility.getPrimaryModel()?.isLoadDone()) {
      return new Promise((resolve) => {
        const onModelRootLoaded = () => {
          facility.eventTarget.removeEventListener(DT_PRIMARY_MODEL_LOADED, onModelRootLoaded);
          setCurrentViewImpl().then(resolve);
        };

        facility.eventTarget.addEventListener(DT_PRIMARY_MODEL_LOADED, onModelRootLoaded);
      });
    }

    await setCurrentViewImpl();
  }
}